import { useConfirm } from 'material-ui-confirm';
import {
  ChangeEvent,
  FormEvent,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  useGetAvailableEmployeeCompetences,
  usePostEmployeeCompetence,
} from '@legacy/apis/employeeCompetencesAPI';
import { IAddCompetenceItems } from '@legacy/apis/employeeCompetencesAPI/types';
import {
  IconButton,
  TextField,
  ToastifyAlert,
  Typography,
} from '@legacy/components/mui-components';
import { ResponseHandler } from '@legacy/components/utils/ResponseHandler';
import { useInitialFocus } from '@legacy/hooks/useInitialFocus';
import { DialogProps } from '@legacy/screens/SystemAdministration/Employees/Competences/components/types';
import { useDebounce } from 'use-debounce';

import { FilterList, Search } from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  List,
  ListItem,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
} from '@mui/material';

import { getKeyboardFocusableElements } from '@legacy/utils/accessibility';
import {
  blurSelectElement,
  keyboardAction,
  reloadKeyboardSelectedElement,
} from '@legacy/utils/keyboardUpDownEvent';
import { eventKeyEnter } from '@legacy/constants/keyboardKey';
import { AddCompetenceItem, NoCompetencesAdded } from './components';

export const AddCompetenceDialog = ({
  closeDialog,
  userId,
  ...rest
}: DialogProps) => {
  const confirm = useConfirm();
  const { t } = useTranslation('employeeCompetences');

  // Initial focus
  const initialFocusElement = useRef<HTMLInputElement | null>(null);
  useInitialFocus(initialFocusElement);

  const {
    nestedData: availableCompetences,
    availableCategories,
    isLoading: availableCompetencesIsLoading,
    isError: availableCompetencesIsError,
  } = useGetAvailableEmployeeCompetences(userId);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedQuery] = useDebounce(searchQuery, 250);

  const [checkedCategories, setCheckedCategories] = useState<
    (number | undefined)[]
  >([]);

  useEffect(() => {
    if (
      availableCategories &&
      availableCategories?.length > 0 &&
      checkedCategories.length === 0
    ) {
      setCheckedCategories(availableCategories?.map(({ groupId }) => groupId));
    }
  }, [availableCategories, setCheckedCategories, checkedCategories]);

  const initialValue = 1;

  // for checking all the availableCategories
  const [initialCategories] = useState(
    availableCategories?.map(({ groupId }) => groupId),
  );

  const { mutate: addCompetence } = usePostEmployeeCompetence();

  const handleSearchQueryChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleToggleCategories = (value: number | undefined) => () => {
    const currentIndex = checkedCategories.indexOf(value);
    const newChecked = [...checkedCategories];

    if (checkedCategories.length === 1 && currentIndex !== -1) return;

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setCheckedCategories(newChecked);
  };

  const [checkedCompetences, setCheckedCompetences] = useState<
    IAddCompetenceItems[]
  >([]);

  const handleToggleCompetences = (id: number) => () => {
    const isAlreadyChecked = checkedCompetences.some(
      (i) => i.competenceId === id,
    );
    let newChecked = [...checkedCompetences];

    if (!isAlreadyChecked) {
      newChecked = [
        ...checkedCompetences,
        { competenceId: id, value: initialValue },
      ];
    } else {
      newChecked = newChecked.filter((i) => i.competenceId !== id);
    }

    setCheckedCompetences(newChecked);
  };

  const cleanUp = () => {
    closeDialog();
    setSearchQuery('');
    setCheckedCompetences([]);
  };

  const [contextMenuAnchorEl, setContextMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const contextMenuIsOpen = Boolean(contextMenuAnchorEl);
  const handleContextMenuClick = (event: MouseEvent<HTMLButtonElement>) => {
    setContextMenuAnchorEl(event.currentTarget);
  };
  const handleContextMenuClose = () => {
    setContextMenuAnchorEl(null);
  };

  const allCategoriesAreChecked =
    checkedCategories.length === availableCategories?.length;

  const handleSave = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    addCompetence(
      {
        userId: parseInt(userId ?? '0', 10),
        employeeCompetences: checkedCompetences,
      },
      {
        onSuccess: () => {
          toast(
            <ToastifyAlert
              description={
                <Trans
                  i18nKey="modals.AddCompetenceToastContent"
                  key="modals.AddCompetenceToastContent"
                  ns="employeeCompetences"
                  defaults={t('modals.AddCompetenceToastContent')}
                  count={checkedCompetences.length}
                />
              }
              color="success"
              data-automation-id="AddCompetenceDialogToastSuccess"
            />,
            {
              autoClose: 2000,
              closeButton: false,
            },
          );
          cleanUp();
        },
        onError: (error) => {
          toast(
            <ToastifyAlert
              title={t('modals.AddCompetenceToastTitleError')}
              description={`${error}`}
              color="error"
              data-automation-id="AddCompetenceDialogToastError"
            />,
            { closeButton: true },
          );
        },
      },
    );
  };

  const handleClose = () => {
    if (checkedCompetences.length < 1) {
      cleanUp();
    } else {
      confirm({
        title: t('modals.AddCompetenceCloseWarningTitle'),
        description: t('modals.AddCompetenceCloseWarningText'),
        dialogProps: {
          maxWidth: 'xs',
        },
        confirmationButtonProps: {
          variant: 'contained',
          size: 'small',
          autoFocus: true,
        },
        cancellationButtonProps: {
          variant: 'outlined',
          size: 'small',
        },
      }).then(({ confirmed }) => {
        if (confirmed) {
          cleanUp();
        }
      });
    }
  };

  const selectedCategoriesLabelText =
    checkedCategories.length > 1
      ? t('modals.AddCompetenceInputSearchMultipleCategoryLabelText', {
          CATEGORY_NAME: availableCategories?.find(
            ({ groupId }) => checkedCategories[0] === groupId,
          )?.groupName,
          CATEGORIES_COUNT: checkedCategories.length - 1,
        })
      : t('modals.AddCompetenceInputSearchOneCategoryLabelText', {
          CATEGORY_NAME: availableCategories?.find(
            ({ groupId }) => checkedCategories[0] === groupId,
          )?.groupName,
        });

  // Filtering data on the client side
  const filteredData = useMemo(() => {
    if (!availableCompetences) return [];
    const filteredRows = availableCompetences
      .map(({ groupId, groupName, items }) => ({
        groupId,
        groupName,
        items: items?.filter((i) =>
          i.competenceName
            ?.toLowerCase()
            .includes(debouncedQuery.toLowerCase()),
        ),
      }))
      .filter((item) => {
        const isQueryMatching = item?.items?.filter((i) =>
          i.competenceName
            ?.toLowerCase()
            .includes(debouncedQuery.toLowerCase()),
        );

        const isCategoryMatching = checkedCategories.includes(item?.groupId);
        return isQueryMatching && isCategoryMatching;
      });

    return filteredRows;
  }, [availableCompetences, debouncedQuery, checkedCategories]);

  const searchContainerResults = useRef<HTMLUListElement>(null);
  const [selectItemIndex, setSelectItemIndex] = useState<number>(-1);

  const onKeyDown = (event: any) => {
    const focusableElements = getKeyboardFocusableElements(
      searchContainerResults?.current,
      "[role='button']",
    );
    const currentSelectedItem = focusableElements[
      selectItemIndex
    ] as HTMLElement;
    if (focusableElements.length > 0) {
      const isNeedToBlur = keyboardAction({
        event,
        setSelectItemIndex,
        selectItemIndex,
        focusableElements,
      });
      if (isNeedToBlur) blurSelectElement(currentSelectedItem);
    }
  };

  reloadKeyboardSelectedElement(
    getKeyboardFocusableElements(
      searchContainerResults?.current,
      "[role='button']",
    )?.[selectItemIndex],
    initialFocusElement,
  );

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      scroll="paper"
      {...rest}
      onClose={handleClose}
      data-automation-id="AddCompetenceDialog"
      PaperProps={{
        component: 'form',
        onSubmit: handleSave,
        method: 'post',
      }}
      onKeyDown={(event) => {
        if (event.key === eventKeyEnter && event.ctrlKey) {
          event.preventDefault();
          handleSave(event as unknown as FormEvent<HTMLFormElement>);
        }
      }}
    >
      <DialogTitle component={Stack}>
        <Stack gap={2}>
          <Typography variant="h3" component="h2">
            {t('modals.AddCompetenceHeader')}
          </Typography>
          <Stack direction="row" alignItems="center">
            <TextField
              size="small"
              label={
                allCategoriesAreChecked
                  ? t('modals.AddCompetenceInputSearchAllLabelText')
                  : selectedCategoriesLabelText
              }
              ariaLabel="Search"
              data-automation-id="AddCompetenceDialogSearchInput"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
              fullWidth
              value={searchQuery}
              onChange={handleSearchQueryChange}
              onKeyDown={onKeyDown}
              inputRef={initialFocusElement}
            />

            <ResponseHandler
              isLoading={availableCompetencesIsLoading}
              isError={availableCompetencesIsError}
            >
              <>
                <IconButton
                  title={t(
                    'modals.AddCompetenceFilterCategoriesButtonTooltipText',
                  )}
                  id="category-context-menu-button"
                  aria-controls="category-context-menu"
                  aria-haspopup="true"
                  aria-expanded={contextMenuIsOpen ? 'true' : 'false'}
                  onClick={handleContextMenuClick}
                  data-automation-id="AddCompetenceDialogFilterButton"
                >
                  <Badge
                    color="primary"
                    invisible={allCategoriesAreChecked}
                    variant="dot"
                    data-automation-id="AddCompetenceDialogFilterButtonBadge"
                  >
                    <FilterList />
                  </Badge>
                </IconButton>

                <Menu
                  id="category-context-menu"
                  anchorEl={contextMenuAnchorEl}
                  open={contextMenuIsOpen}
                  onClose={handleContextMenuClose}
                  MenuListProps={{
                    'aria-labelledby': 'category-context-menu-button',
                  }}
                  data-automation-id="AddCompetenceDialogFilterMenu"
                >
                  <MenuItem
                    onClick={() =>
                      setCheckedCategories(initialCategories || [])
                    }
                    data-automation-id="AddCompetenceDialogFilterSelectAll"
                    disabled={
                      availableCategories?.length === checkedCategories.length
                    }
                  >
                    {t(
                      'modals.AddCompetenceFilterCategoriesSelectAllButtonText',
                    )}
                  </MenuItem>
                  {availableCategories?.map(({ groupId, groupName }) => (
                    <MenuItem
                      key={groupId}
                      sx={{ py: 0 }}
                      onClick={handleToggleCategories(groupId)}
                      disabled={
                        checkedCategories.length === 1 &&
                        checkedCategories[0] === groupId
                      }
                    >
                      <FormControlLabel
                        sx={{ mr: 0, pointerEvents: 'none' }}
                        control={
                          <Switch
                            tabIndex={-1}
                            edge="end"
                            checked={checkedCategories.indexOf(groupId) !== -1}
                            sx={{ mx: 0 }}
                          />
                        }
                        label={groupName}
                      />
                    </MenuItem>
                  ))}
                </Menu>
              </>
            </ResponseHandler>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <ResponseHandler
          isLoading={availableCompetencesIsLoading}
          isError={availableCompetencesIsError}
          isEmpty={availableCompetences.length === 0}
          EmptyComponent={
            <NoCompetencesAdded
              availableCompetences={availableCompetences.length}
            />
          }
          LoadingComponent={
            <Stack gap={2}>
              <Stack direction="row" gap={4}>
                <Skeleton variant="rounded" width={40} height={40} />
                <Skeleton variant="rounded" width="100%" height={40} />
              </Stack>
              <Stack direction="row" gap={4}>
                <Skeleton variant="rounded" width={40} height={40} />
                <Skeleton variant="rounded" width="100%" height={40} />
              </Stack>
              <Stack direction="row" gap={4}>
                <Skeleton variant="rounded" width={40} height={40} />
                <Skeleton variant="rounded" width="100%" height={40} />
              </Stack>
            </Stack>
          }
        >
          <Stack
            gap={2}
            component={List}
            disablePadding
            ref={searchContainerResults}
          >
            {filteredData.filter((i) => i.items && i?.items?.length > 0)
              .length > 0 ? (
              filteredData?.map((item) => {
                const { groupId, groupName, items } = item;
                if (items?.length === 0) return null;
                return (
                  <ListItem disablePadding key={groupId}>
                    <FormControl component="fieldset" fullWidth>
                      <Stack gap={1}>
                        <Typography
                          component="legend"
                          variant="h4"
                          fontWeight={500}
                        >
                          {groupName}
                        </Typography>

                        <Box>
                          {items?.map(
                            ({
                              competenceId: itemId,
                              competenceName: itemName,
                            }) => {
                              const labelId = `checkbox-list-label-${itemId}`;
                              const isChecked =
                                checkedCompetences.filter(
                                  (i) => i.competenceId === itemId,
                                ).length > 0;

                              return (
                                <AddCompetenceItem
                                  key={`${groupId}-${itemId}`}
                                  id={itemId}
                                  labelId={labelId}
                                  name={itemName ?? ''}
                                  isChecked={isChecked}
                                  initialLevel={initialValue}
                                  handleToggleCompetences={() =>
                                    handleToggleCompetences(itemId ?? 0)
                                  }
                                  debouncedQuery={debouncedQuery}
                                  setCheckedCompetence={setCheckedCompetences}
                                  checkedCompetences={checkedCompetences}
                                />
                              );
                            },
                          )}
                        </Box>
                      </Stack>
                    </FormControl>
                  </ListItem>
                );
              })
            ) : (
              <Typography>{t('modals.AddCompetenceNoResultsText')}</Typography>
            )}
          </Stack>
        </ResponseHandler>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          variant="outlined"
          data-automation-id="AddCompetenceDialogCancelButton"
        >
          {t('modals.AddCompetenceButtonCancelButtonText')}
        </Button>
        <Button
          type="submit"
          disabled={checkedCompetences.length < 1}
          variant="contained"
          data-automation-id="AddCompetenceDialogAddButton"
        >
          {t('modals.AddCompetenceButtonSaveButtonText')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
